import { DocumentModel } from "../../models/document.model";
import * as angular from "angular";
import {
    mapThemeConstants,
    mapViewControlsConstants,
} from "../../utils/constants/map.constants";
import { Campaign } from "../../models/campaign.model";
import { CampaignDataProvider } from "../../data-provider/campaign.dp";
import { DocumentDataProvider } from "../../data-provider/document.dp";
import { Group } from "../../models/group.model";
import { FileUploader } from "angular-file-upload";
import { ToastUtils } from "../../helper/toast.helper";

declare const google;

export class ViewOptionsService {
    private readonly $filter: any;
    private readonly $timeout: any;
    private toast: ToastUtils;
    private activeCampaign: Campaign;
    private kmlLayers: any[] = [];
    private documentDataProvider: DocumentDataProvider;
    private campaignDataProvider: CampaignDataProvider;
    private uploader: FileUploader;
    public gmap: any;
    public kmlFiles: DocumentModel[] = [];
    public areAllChecked: boolean = false;
    private groupSelectedCount: number = 0;
    public groupSelectionMap: { [key: number]: any } = {};
    public groupSearch: string = "";

    public loadingKML: boolean;
    public viewOptions = {
        theme: "roadmap",
        shouldCluster: true,
        maxMarkersBeforeCluster: 5000,
        markers: true,
        userMarkers: true,
        sideNavWidth: null,
        groups: [],
        drawingMode: "polygon",
        mapStyleControls: {
            roads: 3,
            landmarks: 3,
            labels: 3,
        },
        kmlIds: [],
        userMarkerType: "last_location",
        showUserMarkerType: false,
    };

    constructor(
        $timeout: any,
        __env: any,
        $filter: any,
        CampaignDataProvider: CampaignDataProvider,
        ToastUtils: ToastUtils,
        DocumentDataProvider: DocumentDataProvider
    ) {
        this.$timeout = $timeout;
        this.activeCampaign = CampaignDataProvider.activeCampaign;
        this.$filter = $filter;
        this.documentDataProvider = DocumentDataProvider;
        this.toast = ToastUtils;
        this.campaignDataProvider = CampaignDataProvider;

        this.uploader = this.documentDataProvider.getFileUploader(4);
        this.uploader.onWhenAddingFileFailed = (item: any) => {
            const fileType = item.name.split(".").pop().toLowerCase();
            const validFileTypes = ["kml", "kmz", "geojson"];
            if (validFileTypes.includes(fileType)) {
                if (item && item.size >= 20 * 1024 * 1024) {
                    this.toast.success("Your file must be smaller than 20MB");
                }
            } else {
                this.toast.error(
                    "Only KML, KMZ, and GeoJSON file types are supported"
                );
            }
        };

        this.uploader.onSuccessItem = (response: object) => {};
        this.uploader.onCompleteItem = (response: object) => {
            this.documentDataProvider.clearAll();
            this.$timeout(() => {
                this.loadKMLDocuments(this.gmap, true);
            }, 1000);
        };

        this.campaignDataProvider.getOrganisations().then(() => {
            if (
                this.campaignDataProvider.activeCampaign?.oid &&
                this.campaignDataProvider.activeCampaign.oid == 386
            ) {
                if (
                    this.campaignDataProvider.activeCampaign &&
                    [2, 3, 8, 9, 10].includes(
                        Number(
                            this.campaignDataProvider.activeCampaign
                                .permissionid
                        )
                    )
                ) {
                    this.viewOptions.showUserMarkerType = true;
                } else {
                    this.viewOptions.userMarkerType = "last_location";
                    this.viewOptions.showUserMarkerType = false;
                }
            } else {
                this.viewOptions.userMarkerType = "last_location";
                this.viewOptions.showUserMarkerType = false;
            }
        });
    }

    getViewOptions(): any {
        return this.viewOptions;
    }

    getUploader(): any {
        return this.uploader;
    }

    setViewOptions(newValue, oldValue, mapOptions) {
        if (newValue.theme != oldValue.theme) {
            if (
                this.viewOptions.theme != "hybrid" &&
                this.viewOptions.theme != "terrain" &&
                this.viewOptions.theme != "roadmap"
            ) {
                mapOptions.mapTypeId = "roadmap";
            } else {
                mapOptions.mapTypeId = angular.copy(this.viewOptions.theme);
            }
        }

        this.activeCampaign.localInfo.map.options = mapOptions;
        this.activeCampaign.localInfo.map.viewOptions = angular.copy(
            this.viewOptions
        );
        mapOptions.styles = this.getMapStyles(this.viewOptions);
        this.activeCampaign.saveLocalStorage();
    }

    toggleKMLLayers(gmap: any) {
        angular.forEach(this.kmlFiles, (kmlFile) => {
            const ids: DocumentModel[] = this.viewOptions.kmlIds.filter(
                (id: any) => kmlFile.dcid == id
            );

            if (ids.length <= 0 && this.kmlLayers[kmlFile.dcid]) {
                if (kmlFile.dctype == "geojson") {
                    const layers = this.kmlLayers[kmlFile.dcid];
                    angular.forEach(layers, (layer: any) => {
                        gmap.data.remove(layer);
                    });
                } else {
                    this.kmlLayers[kmlFile.dcid].setMap(null);
                }
                delete this.kmlLayers[kmlFile.dcid];
            }
        });

        angular.forEach(this.viewOptions.kmlIds, (id) => {
            const files: DocumentModel[] = this.kmlFiles.filter(
                (kmlFile) => kmlFile.dcid == id
            );

            if (files.length > 0 && !this.kmlLayers[id]) {
                const fileType = files[0].dctype;
                if (fileType === "geojson") {
                    this.loadGeoJsonLayer(files[0], gmap);
                } else {
                    files[0].getUrl().then(() => {
                        const kmlLayer = new google.maps.KmlLayer(
                            files[0].url,
                            {
                                suppressInfoWindows: true,
                                preserveViewport: true,
                                clickable: true,
                                map: gmap,
                            }
                        );
                        this.kmlLayers[id] = kmlLayer;
                    });
                }
            }
        });

        return this.kmlLayers;
    }

    setGmap(gmap: any) {
        this.gmap = gmap;
    }

    loadKMLDocuments(gmap: any, checkNew: boolean = false): void {
        this.loadingKML = true;
        this.documentDataProvider
            .getDocumentsByType("4")
            .then((docs: DocumentModel[]) => {
                this.loadingKML = false;
                this.kmlFiles.splice(0, this.kmlFiles.length);
                angular.forEach(docs, (doc: DocumentModel) => {
                    this.kmlFiles.push(doc); // Still handle KML and geojson files
                });
                if (checkNew) {
                    this.viewOptions.kmlIds.push(this.kmlFiles[0].dcid);
                } else {
                    this.toggleKMLLayers(gmap);
                }
            });
    }

    loadGeoJsonLayer(geoFile: DocumentModel, gmap: any): void {
        if (geoFile) {
            geoFile.getData().then((data: object) => {
                const layer = gmap.data.addGeoJson(data);
                this.kmlLayers[geoFile.dcid] = layer;
                this.setGeoJsonStyle(true);
            });
        }
    }

    setGeoJsonStyle(interactive: boolean): any {
        this.gmap.data.setStyle((feature: any) => {
            const geometryType = feature.getGeometry().getType();
            const name = feature.getProperty("name"); // Get the name property from each feature
            let style: any = {
                clickable: interactive,
                zIndex: interactive ? 1 : -1,
                cursor: interactive ? "pointer" : "crosshair",
            };
            if (geometryType === "Point") {
                let zoom = this.gmap.getZoom();
                style.icon = {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 0,
                    fillOpacity: 0,
                    strokeOpacity: 0,
                };
                if (zoom >= 13) {
                    style.label = {
                        text: name,
                        fontSize: "12pt",
                        fontWeight: "bold",
                        color: "black",
                    };
                }
                return style;
            } else if (
                geometryType === "Polygon" ||
                geometryType === "MultiPolygon"
            ) {
                // Get styling from GeoJSON properties
                const fillColor = feature.getProperty("fill") || "green";
                const fillOpacity = feature.getProperty("fill-opacity") || 0.6;
                const strokeColor = feature.getProperty("stroke") || "#333333";
                const strokeWeight = feature.getProperty("stroke-width") || 1;
                const strokeOpacity =
                    feature.getProperty("stroke-opacity") || 1;

                return {
                    ...style,
                    fillColor: fillColor,
                    fillOpacity: fillOpacity,
                    strokeColor: strokeColor,
                    strokeWeight: strokeWeight,
                    strokeOpacity: strokeOpacity,
                };
            }
        });
    }

    getMapStyles(viewOptions: any): any[] {
        let styles = [
            ...mapViewControlsConstants.roads[
                viewOptions.mapStyleControls.roads ?? 3
            ],
            ...mapViewControlsConstants.landmarks[
                viewOptions.mapStyleControls.landmarks ?? 3
            ],
            ...mapViewControlsConstants.labels[
                viewOptions.mapStyleControls.labels ?? 3
            ],
        ];

        if (
            viewOptions.theme != "hybrid" &&
            viewOptions.theme != "terrain" &&
            viewOptions.theme != "roadmap"
        ) {
            styles = [
                ...styles,
                ...mapThemeConstants[viewOptions.theme ?? "roadmap"],
            ];
        }

        return styles;
    }

    roadControlOptionText(amount: number): string {
        let controlOptions = [
            "No roads",
            "Major roads only",
            "Most roads",
            "All roads",
        ];

        return controlOptions[amount];
    }

    landmarkControlOptionText(amount: number): string {
        let controlOptions = [
            "No places of interest",
            "Few places of interest",
            "Most places of interest",
            "All places of interest",
        ];

        return controlOptions[amount];
    }

    labelControlOptionText(amount: number): string {
        let controlOptions = [
            "No Labels",
            "Some Labels",
            "Most Labels",
            "All labels",
        ];

        return controlOptions[amount];
    }

    initGroups(polygonGroups: Group[]): void {
        if (!polygonGroups) return;

        const oldOptionsMap = new Map(
            this.viewOptions.groups.map((g) => [g.gid, g])
        );

        this.viewOptions.groups = [];
        this.groupSelectionMap = {};
        this.groupSelectedCount = 0;

        polygonGroups.forEach((group: any) => {
            const gid = (group as any).gid;

            let option = oldOptionsMap.get(gid);
            if (!option) {
                option = { gid: gid, selected: true };
            }
            group._formattedDate = this.niceShortDateTime(group.created);
            this.viewOptions.groups.push(option);
            this.groupSelectionMap[gid] = option;

            // D. Update counts
            if (option.selected) {
                this.groupSelectedCount++;
            }
        });

        this.areAllChecked =
            polygonGroups.length > 0 &&
            this.groupSelectedCount === polygonGroups.length;
    }

    handleSingleSelectionChange(option: any, allGroupsLength: number): void {
        if (option.selected) {
            this.groupSelectedCount++;
        } else {
            this.groupSelectedCount--;
        }
        this.areAllChecked = this.groupSelectedCount === allGroupsLength;
    }

    toggleAllGroups(polygonGroups: Group[]): void {
        const targetState = !this.areAllChecked;

        polygonGroups.forEach((g) => {
            const gid = (g as any).gid;
            if (this.groupSelectionMap[gid]) {
                this.groupSelectionMap[gid].selected = targetState;
            }
        });

        this.areAllChecked = targetState;
        this.groupSelectedCount = targetState ? polygonGroups.length : 0;
    }

    shapeFileExists(id: any, ids: string | any[]): boolean {
        return ids.includes(id);
    }

    toggleShapeFile(id: any, ids: any[]): void {
        let idx: number = -1;

        angular.forEach(ids, (thisId: any, $index: number) => {
            if (id == thisId) {
                idx = $index;
            }
        });

        if (idx > -1) {
            ids.splice(idx, 1);
        } else {
            ids.push(id);
        }
    }

    niceShortDateTime(value: string) {
        if (!value) {
            return "";
        }
        value = value.replace(/-/g, "/");
        const valueDate = new Date(value);

        const datePart = this.$filter("date")(valueDate, "MMM dd");
        const timePart = this.$filter("timeFormat")(valueDate);

        return `${datePart} - ${timePart}`;
    }

    resetKMLLayers(): void {
        this.kmlLayers = [];
    }
}

angular.module("map").service("ViewOptionsService", ViewOptionsService);
